// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { Incentive, ProgramLookupState, ProgramsState } from '@makemydeal/dr-dash-types';
import type { IResponsePayment, OfferType } from '@makemydeal/dr-platform-types';
import { CASH } from '@makemydeal/dr-platform-types';
import type { Reducer } from 'redux';
import { initActionTypes } from '@makemydeal/dr-shared-store';

import {
    PROGRAMS_CLEAR_ALL,
    PROGRAMS_CLEAR_INCENTIVES,
    PROGRAMS_CLEAR_SELECTED,
    PROGRAMS_INCENTIVES_REQUEST_FAILURE,
    PROGRAMS_SELECT,
    PROGRAMS_SET_CREDIT_SCORE_OVERRIDE,
    REQUEST_PROGRAMS,
    REQUEST_PROGRAMS_FAILURE,
    REQUEST_PROGRAMS_SUCCESS,
    TOGGLE_INCENTIVE_FOR_PROGRAM,
    SET_RETAINED_PROGRAM_DISPLAY,
    CLEAR_RETAINED_PROGRAM_DISPLAY,
    HOLD_PROGRAM_FALLBACK_VALUES,
    RELEASE_PROGRAM_FALLBACK_VALUES,
    MARK_PROGRAM_LOOKUP_PENDING,
    DISCARD_PROGRAM_LOOKUP_PENDING,
    RESOLVE_PROGRAM_LOOKUP,
    SET_PENDING_APPLY_PROGRAM,
    CLEAR_PENDING_APPLY_PROGRAM,
    SET_APPLIED_PAYMENT_BASELINE,
    CLEAR_APPLIED_PAYMENT_BASELINE,
    SET_ALL_TIERS_LOADED,
    ALL_PROGRAMS_LOADED,
    DISABLE_PROGRAM_LOSS_INCENTIVES,
    CLEAR_PROGRAM_LOSS_INCENTIVES,
    PRUNE_PROGRAM_LOSS_INCENTIVES,
    MARK_PROGRAM_AUTO_SELECTED,
    CLEAR_PROGRAM_AUTO_SELECTED
} from '../actionTypes/offerActionTypes';

export type ProgramsReducer = Reducer<ProgramsState>;

/**
 * Applying a program settles the displayed row, so its not-lender-backed record goes with it. An
 * in-flight lookup's `pending` marker has to survive: dropping it leaves `RESOLVE_PROGRAM_LOOKUP` on
 * its `!pending` path, which discards a `NO_VALID_QUOTES` outcome and loses the Term-field message.
 */
const withoutSettledLookup = (lookup?: ProgramLookupState): ProgramLookupState | undefined =>
    lookup?.pending ? { pending: lookup.pending } : undefined;

export const initialState: ProgramsState = {};

const isSupersededTierResponse = (state: ProgramsState, meta: AnyFSA['meta']): boolean =>
    meta?.requestedTier != null && state.allTiersInputsKey != null && meta.inputsKey !== state.allTiersInputsKey;

/**
 * Used to mod the existing selection when toggling them on/off in the UX
 */
const updateSelectedIncentives = (
    state: ProgramsState,
    { incentive, selected }: { incentive: Incentive; selected: boolean }
): number[] => {
    const list = state.selectedIncentives ?? []; // useful ref
    const indexOfExisting = list.indexOf(incentive.incentiveId); // index if it exists

    const shouldAdd = indexOfExisting < 0 && selected; // needs to be added to the list
    const shouldRemove = indexOfExisting >= 0 && !selected; // needs to be removed

    if (shouldAdd) list.push(incentive.incentiveId);
    else if (shouldRemove) list.splice(indexOfExisting, 1);

    return list;
};

export const reducer: ProgramsReducer = (state = initialState, action: AnyFSA) => {
    switch (action.type) {
        case PROGRAMS_CLEAR_ALL: {
            return {
                ...initialState,
                retainedProgramDisplay: state.retainedProgramDisplay,
                autoSelectedProgramByOfferType: state.autoSelectedProgramByOfferType,
                creditScoreOverride: state.creditScoreOverride,
                appliedCreditScoreOverride: state.appliedCreditScoreOverride
            };
        }

        case 'APPLIED_PROGRAM': {
            const appliedOverride = action.meta?.creditScoreOverride;
            return {
                ...state,
                appliedCreditScoreOverride: appliedOverride,
                programLookup: withoutSettledLookup(state.programLookup),
                ...(appliedOverride != null && { creditScoreOverride: appliedOverride })
            };
        }

        case PROGRAMS_SET_CREDIT_SCORE_OVERRIDE: {
            return { ...state, creditScoreOverride: action.payload };
        }

        case 'UPDATED_MANUAL_CREDIT_SCORE': {
            return { ...state, creditScoreOverride: undefined };
        }

        case SET_RETAINED_PROGRAM_DISPLAY: {
            const { offerType, programName, lenderLegalName, program } = action.payload;
            return {
                ...state,
                retainedProgramDisplay: {
                    ...state.retainedProgramDisplay,
                    [offerType]: { programName, lenderLegalName, ...(program && { program }) }
                }
            };
        }

        case CLEAR_RETAINED_PROGRAM_DISPLAY: {
            const offerType: OfferType | undefined = action.payload?.offerType;
            if (!offerType) return { ...state, retainedProgramDisplay: undefined };
            const retainedProgramDisplay = { ...state.retainedProgramDisplay };
            delete retainedProgramDisplay[offerType];
            return { ...state, retainedProgramDisplay };
        }

        // Held while a program lookup is in flight, released once its outcome is known. Deliberately not
        // preserved through PROGRAMS_CLEAR_ALL: that fires on a vehicle change, and values held for the
        // previous vehicle should not be priced against the new one.
        case HOLD_PROGRAM_FALLBACK_VALUES: {
            return { ...state, programFallbackHold: action.payload };
        }

        case RELEASE_PROGRAM_FALLBACK_VALUES: {
            return state.programFallbackHold ? { ...state, programFallbackHold: undefined } : state;
        }

        case MARK_PROGRAM_LOOKUP_PENDING: {
            return { ...state, programLookup: { pending: action.payload } };
        }

        case DISCARD_PROGRAM_LOOKUP_PENDING: {
            const lookup = state.programLookup;
            if (!lookup?.pending) return state;
            if (lookup.pending.requestId !== undefined && action.payload.requestId !== lookup.pending.requestId) {
                return state;
            }
            // Only the pending goes: a settled no-program record describes a lookup that did resolve.
            const { pending: _discarded, ...rest } = lookup;
            return { ...state, programLookup: rest };
        }

        case RESOLVE_PROGRAM_LOOKUP: {
            const pending = state.programLookup?.pending;
            const { noProgram, requestId } = action.payload;
            if (!pending) {
                if (noProgram) return state;
                return state.programLookup?.noProgram ? { ...state, programLookup: {} } : state;
            }
            // Only the lookup that was marked can resolve its own marker. An outcome from a different
            // lookup, or one carrying no id at all (the checkbox runs its own unmarked lookup), says
            // nothing about this one.
            if (pending.requestId !== undefined && requestId !== pending.requestId) {
                return state;
            }
            return { ...state, programLookup: noProgram ? { noProgram: pending } : {} };
        }

        // A different deal can arrive here in the same store, and the record describes the one that
        // produced it. PROGRAMS_CLEAR_ALL fires on a vehicle change and whenever Programs & Incentives
        // closes, never on a deal load, so it cannot cover this.
        case initActionTypes.INIT_SUCCESS: {
            return state.programLookup || state.autoSelectedProgramByOfferType
                ? { ...state, programLookup: undefined, autoSelectedProgramByOfferType: undefined }
                : state;
        }

        case MARK_PROGRAM_AUTO_SELECTED: {
            const { offerType, lenderProgram } = action.payload;
            return {
                ...state,
                autoSelectedProgramByOfferType: { ...state.autoSelectedProgramByOfferType, [offerType]: lenderProgram }
            };
        }

        case CLEAR_PROGRAM_AUTO_SELECTED: {
            const offerType: OfferType = action.payload;
            if (!state.autoSelectedProgramByOfferType?.[offerType]) return state;
            return {
                ...state,
                autoSelectedProgramByOfferType: { ...state.autoSelectedProgramByOfferType, [offerType]: undefined }
            };
        }

        case PROGRAMS_CLEAR_SELECTED: {
            return { ...state, selectedIncentives: undefined, selectedProgram: undefined };
        }

        case PROGRAMS_CLEAR_INCENTIVES: {
            return state;
        }

        case SET_PENDING_APPLY_PROGRAM: {
            return { ...state, pendingApplyProgram: action.payload };
        }

        case CLEAR_PENDING_APPLY_PROGRAM: {
            return { ...state, pendingApplyProgram: undefined };
        }

        case SET_APPLIED_PAYMENT_BASELINE: {
            return { ...state, appliedPaymentBaseline: action.payload };
        }

        case CLEAR_APPLIED_PAYMENT_BASELINE: {
            if (state.appliedPaymentBaseline == null) return state;
            return { ...state, appliedPaymentBaseline: undefined };
        }

        case SET_ALL_TIERS_LOADED: {
            const keepsIncentiveSelection = action.meta?.isInputsReload === true;
            return {
                ...state,
                programs: [],
                ...(!keepsIncentiveSelection && { selectedIncentives: undefined, programLossDisabledIds: undefined }),
                allTiersLoadedForOfferType: action.payload,
                allTiersInputsKey: action.meta?.inputsKey,
                loadedTierCount: 0,
                allProgramsLoaded: false,
                primaryTierLoaded: false
            };
        }

        case DISABLE_PROGRAM_LOSS_INCENTIVES: {
            const incoming = (action.payload as number[]) ?? [];
            return {
                ...state,
                programLossDisabledIds: Array.from(new Set([...(state.programLossDisabledIds ?? []), ...incoming]))
            };
        }

        case CLEAR_PROGRAM_LOSS_INCENTIVES: {
            if (state.programLossDisabledIds == null) return state;
            return { ...state, programLossDisabledIds: undefined };
        }

        case PRUNE_PROGRAM_LOSS_INCENTIVES: {
            if (state.programLossDisabledIds == null) return state;
            const available = new Set((action.payload as number[]) ?? []);
            const remaining = state.programLossDisabledIds.filter((id) => !available.has(id));
            if (remaining.length === state.programLossDisabledIds.length) return state;
            return { ...state, programLossDisabledIds: remaining.length ? remaining : undefined };
        }

        case ALL_PROGRAMS_LOADED: {
            return { ...state, allProgramsLoaded: true };
        }

        case PROGRAMS_SELECT: {
            if (action.meta?.enableProgramIncentivesRestoration) {
                return state;
            }
            return {
                ...state,
                selectedProgram: action.payload,
                selectedIncentives: action.payload.appliedIncentiveIds
            };
        }

        case TOGGLE_INCENTIVE_FOR_PROGRAM: {
            const selectedIncentives = updateSelectedIncentives(state, action.payload);
            return { ...state, selectedIncentives };
        }

        case REQUEST_PROGRAMS: {
            return state;
        }

        case REQUEST_PROGRAMS_SUCCESS: {
            const { currentOfferType, currentSelectedProgram, creditScoreOverride, requestedTier, primary } = action.meta;
            const responsePayment = action.payload[currentOfferType];
            // Cash has exactly one "program" (there's no per-lender list), so a cash-only response
            // is the payment object itself rather than a { payments: [...] } wrapper. Treat it as a
            // single-row program list instead of dropping it, or the table has no row to expand into
            // and the incentives pick-list (rendered as that row's expanded content) never shows.
            // This reducer has no direct state access, so the toggle check happens where the action
            // is dispatched (programActionCreators.ts) and is threaded through via action.meta.
            // Beyond null/undefined, also screen out an empty object - a cash node that carries no
            // payment or incentive data at all shouldn't be wrapped into a row and marked as a
            // successful load.
            const hasCashPaymentContent = Boolean(responsePayment) && Object.keys(responsePayment).length > 0;
            const rawPayments = (responsePayment?.payments ??
                (currentOfferType === CASH && hasCashPaymentContent && action.meta.isCashDealIncentivesFixesEnabled
                    ? [responsePayment]
                    : [])) as IResponsePayment[];
            const isTierCall = requestedTier != null;

            if (isTierCall && state.allTiersLoadedForOfferType != null && currentOfferType !== state.allTiersLoadedForOfferType) {
                return state;
            }
            if (isSupersededTierResponse(state, action.meta)) return state;

            const tagged = isTierCall ? rawPayments.map((payment) => ({ ...payment, requestedTier })) : rawPayments;
            const programs = isTierCall
                ? [
                      ...(state.programs ?? []).filter(
                          (p) => (p as IResponsePayment & { requestedTier?: string }).requestedTier !== requestedTier
                      ),
                      ...tagged
                  ]
                : tagged;

            const loadedTierCount = isTierCall ? (state.loadedTierCount ?? 0) + 1 : state.loadedTierCount;

            if (isTierCall && !primary) {
                return { ...state, programs, programsError: undefined, loadedTierCount };
            }

            return {
                ...state,
                programs,
                selectedProgram: action.meta.enableProgramIncentivesRestoration ? undefined : currentSelectedProgram,
                programsError: undefined,
                creditScoreOverride,
                primaryTierLoaded: true,
                loadedTierCount
            };
        }

        case REQUEST_PROGRAMS_FAILURE: {
            if (isSupersededTierResponse(state, action.meta)) return state;
            const { requestedTier, primary } = action.meta ?? {};
            const isTierCall = requestedTier != null;
            const loadedTierCount = isTierCall ? (state.loadedTierCount ?? 0) + 1 : state.loadedTierCount;
            if (isTierCall && !primary) {
                return { ...state, loadedTierCount };
            }
            const base = { ...state, programsError: action.payload, loadedTierCount };
            return primary ? { ...base, primaryTierLoaded: true } : base;
        }

        case PROGRAMS_INCENTIVES_REQUEST_FAILURE: {
            return { ...state, incentivesError: action.payload };
        }

        default: {
            return state;
        }
    }
};
